import React, { createContext, useContext, useEffect, useState } from 'react';

interface ContentSpaceContextType {
  contentWidth: number;
  isCompact: boolean; // < 900px available
  isMedium: boolean; // 900px - 1200px available
  isWide: boolean; // > 1200px available
}

const ContentSpaceContext = createContext<ContentSpaceContextType>({
  contentWidth: 0,
  isCompact: true,
  isMedium: false,
  isWide: false
});

export const useContentSpace = () => {
  const context = useContext(ContentSpaceContext);
  if (!context) {
    throw new Error('useContentSpace must be used within ContentSpaceProvider');
  }
  return context;
};

export const ContentSpaceProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [contentWidth, setContentWidth] = useState(0);

  useEffect(() => {
    let lastWidth = 0;

    const updateContentWidth = () => {
      const contentElement = document.querySelector(
        '.content-scroll-container'
      ) as HTMLElement;
      if (contentElement) {
        const width = contentElement.offsetWidth;
        if (width !== lastWidth) {
          lastWidth = width;
          setContentWidth(width);
        }
      }
    };

    // Initial measurement
    updateContentWidth();

    // Use a combination of ResizeObserver and periodic checking
    let resizeObserver: ResizeObserver | null = null;
    let intervalId: NodeJS.Timeout | null = null;

    // Set up ResizeObserver
    const setupObserver = () => {
      const contentElement = document.querySelector(
        '.content-scroll-container'
      ) as HTMLElement;
      if (contentElement) {
        resizeObserver = new ResizeObserver(() => {
          updateContentWidth();
        });
        resizeObserver.observe(contentElement);
      }
    };

    // Try to set up observer immediately and with retries
    setupObserver();
    setTimeout(setupObserver, 100);
    setTimeout(setupObserver, 500);

    // Also use a periodic check as backup (every 100ms)
    intervalId = setInterval(updateContentWidth, 100);

    // Listen to window resize
    window.addEventListener('resize', updateContentWidth);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
      window.removeEventListener('resize', updateContentWidth);
    };
  }, []);

  // Define breakpoints based on available content space, not viewport
  const isCompact = contentWidth < 900;
  const isMedium = contentWidth >= 900 && contentWidth < 1200;
  const isWide = contentWidth >= 1200;

  return (
    <ContentSpaceContext.Provider
      value={{
        contentWidth,
        isCompact,
        isMedium,
        isWide
      }}
    >
      {children}
    </ContentSpaceContext.Provider>
  );
};

// Custom hook for content-aware responsive design
export const useContentResponsive = () => {
  const { isCompact, isMedium, isWide } = useContentSpace();

  return {
    // Layout helpers
    shouldStackVertically: isCompact,
    shouldUseTabs: !isWide,
    shouldShowSideBySide: isWide,

    // Grid sizes based on content space
    getGridSizes: (desktop: number, tablet?: number, mobile?: number) => {
      if (isWide) return desktop;
      if (isMedium) return tablet ?? desktop;
      return mobile ?? tablet ?? desktop;
    }
  };
};
